import { useState, useRef, useEffect } from 'react';
import { ParentSidebarNavItem } from '../../../types';

export const AI_WEBSITE_AGENT = "AI Website Agent";
export const AI_CAMPAIGN_AGENT = "AI Campaign Agent";
export const ALWAYS_ON_RECRUITER = "Always-On Recruiter";
export const ANALYTICS = "Analytics";
export const AI_CONFIGURATION = "AI Configuration";

interface CurrentUser {
  id: string;
  isEnrollmentMarketerExpanded?: boolean;
  isStudentRecruiterExpanded?: boolean;
  isAnalyticsExpanded?: boolean;
  isCampaignAgentExpanded?: boolean;
  isAiConfigurationExpanded?: boolean;
}

interface UseSidebarStateProps {
  navItems: Array<ParentSidebarNavItem>;
  isInitiallyExpanded: boolean;
  currentUser: CurrentUser;
  formAuthenticityToken: string;
}

interface PersistedItemConfig {
  userField: keyof Omit<CurrentUser, 'id'>;
  updateParam: string;
}

// Maps sidebar item display names to the user preference field and the
// update-endpoint param name. The DB columns and corresponding camelCase
// user fields kept their original names (enrollment_marketer for
// AI Website Agent, student_recruiter for Always-On Recruiter) to avoid
// a column rename migration — this map is the single place that records
// the translation between current UI names and legacy persistence names.
const PERSISTED_SIDEBAR_ITEMS: Record<string, PersistedItemConfig> = {
  [AI_WEBSITE_AGENT]: {
    userField: 'isEnrollmentMarketerExpanded',
    updateParam: 'is_enrollment_marketer_expanded',
  },
  [AI_CAMPAIGN_AGENT]: {
    userField: 'isCampaignAgentExpanded',
    updateParam: 'is_campaign_agent_expanded',
  },
  [ALWAYS_ON_RECRUITER]: {
    userField: 'isStudentRecruiterExpanded',
    updateParam: 'is_student_recruiter_expanded',
  },
  [ANALYTICS]: {
    userField: 'isAnalyticsExpanded',
    updateParam: 'is_analytics_expanded',
  },
  [AI_CONFIGURATION]: {
    userField: 'isAiConfigurationExpanded',
    updateParam: 'is_ai_configuration_expanded',
  },
};

export const useSidebarState = ({
  navItems,
  isInitiallyExpanded,
  currentUser,
  formAuthenticityToken
}: UseSidebarStateProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(!isInitiallyExpanded);
  const [isCollapsing, setIsCollapsing] = useState<boolean>(!isInitiallyExpanded);
  const [isClickingArrow, setIsClickingArrow] = useState<boolean>(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    arrowRef.current?.addEventListener('mousedown', () =>
      setIsClickingArrow(true)
    );
    arrowRef.current?.addEventListener('mouseup', () =>
      setIsClickingArrow(false)
    );
    return () => {
      arrowRef.current?.removeEventListener('mousedown', () =>
        setIsClickingArrow(true)
      );
      arrowRef.current?.removeEventListener('mouseup', () =>
        setIsClickingArrow(false)
      );
    };
  }, [arrowRef]);

  const startCollapse = (collapseCallback: () => void) => {
    setIsCollapsing(true);
    collapseCallback();
    setTimeout(() => setIsCollapsed(true), 300);
  };

  const startExpand = (expandCallback: () => void) => {
    expandCallback();
    setIsCollapsed(false);
    setTimeout(() => setIsCollapsing(false), 10);
  };

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(() =>
    navItems.reduce<Record<string, boolean>>((acc, item) => {
      const itemName = String(item.name);
      if (item.subItems && item.subItems.some((i) => i.current)) {
        acc[itemName] = true;
        return acc;
      }
      const config = PERSISTED_SIDEBAR_ITEMS[itemName];
      acc[itemName] = config ? currentUser[config.userField] ?? false : false;
      return acc;
    }, {})
  );

  const toggleItem = (itemName: string) => {
    setExpandedItems((prev) => {
      const newState = {
        ...prev,
        [itemName]: !prev[itemName],
      };

      const config = PERSISTED_SIDEBAR_ITEMS[itemName];
      if (config) {
        const route = `/users/${currentUser.id}/update-sidebar-preferences`;
        const options = {
          method: 'PUT',
          headers: new Headers({
            'Content-Type': 'application/json; charset=UTF-8',
          }),
          body: JSON.stringify({
            [config.updateParam]: !prev[itemName],
            authenticity_token: formAuthenticityToken,
          }),
        };

        fetch(route, options).catch(error => {
          console.error('Error updating sidebar preferences:', error);
        });
      }

      return newState;
    });
  };

  return {
    isCollapsed,
    isCollapsing,
    isClickingArrow,
    arrowRef,
    expandedItems,
    startCollapse,
    startExpand,
    toggleItem
  };
};

export default useSidebarState;
